import { useCallback, useEffect, useState } from 'react';
import { summarizeExpensesForBranch } from '../../infra/expensesRepoRead';
import { summarizeBranchSettlementsLocalQuery } from '../../infra/expensesRepoSettlementsLocal';
import type {
  ExpenseListQuery,
  ExpenseListSummary,
  ExpenseSettlementsListQuery,
  ExpenseSettlementsSummary,
} from '../../domain/expenseTypes';
import { captureOpsFailure } from '../../../shared/sentry/sentryOps';

const EMPTY_EXPENSE_SUMMARY: ExpenseListSummary = {
  count: 0,
  total: 0,
  unpaidTotal: 0,
  overdueCount: 0,
  overdueTotal: 0,
};

const EMPTY_PAYMENTS_SUMMARY: ExpenseSettlementsSummary = {
  count: 0,
  totalPaid: 0,
  cashOutflowTotal: 0,
};

/**
 * KPIs de la pestaña Facturas. Recibe el MISMO listQuery que arma la lista
 * (branch + período + estado + categoría + proveedor + búsqueda + vencidas):
 * los tiles siempre describen al conjunto visible.
 */
export function useExpenseBillsKpi({
  moduleEnabled,
  listQuery,
}: {
  moduleEnabled: boolean;
  listQuery: ExpenseListQuery | null;
}) {
  const [summary, setSummary] = useState<ExpenseListSummary>(EMPTY_EXPENSE_SUMMARY);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!moduleEnabled || !listQuery) {
      setSummary(EMPTY_EXPENSE_SUMMARY);
      setLoading(false);
      setError(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const next = await summarizeExpensesForBranch(listQuery);
      setSummary(next);
    } catch (e) {
      captureOpsFailure({ module: 'expenses', op: 'kpi.bills.refresh', error: e });
      setSummary(EMPTY_EXPENSE_SUMMARY);
      setError(e instanceof Error ? e.message : 'No pude cargar los totales.');
    } finally {
      setLoading(false);
    }
  }, [moduleEnabled, listQuery]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return { summary, loading, error, refresh };
}

/**
 * KPIs de la pestaña Pagos. Igual que facturas: misma query que la lista
 * (período + método + estado + búsqueda).
 */
export function useExpensePaymentsKpi({
  moduleEnabled,
  query,
}: {
  moduleEnabled: boolean;
  query: Omit<ExpenseSettlementsListQuery, 'limit' | 'offset'> | null;
}) {
  const [summary, setSummary] = useState<ExpenseSettlementsSummary>(EMPTY_PAYMENTS_SUMMARY);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!moduleEnabled || !query) {
      setSummary(EMPTY_PAYMENTS_SUMMARY);
      setLoading(false);
      setError(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const next = await summarizeBranchSettlementsLocalQuery(query);
      setSummary(next);
    } catch (e) {
      captureOpsFailure({ module: 'expenses', op: 'kpi.payments.refresh', error: e });
      setSummary(EMPTY_PAYMENTS_SUMMARY);
      setError(e instanceof Error ? e.message : 'No pude cargar los totales.');
    } finally {
      setLoading(false);
    }
  }, [moduleEnabled, query]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return { summary, loading, error, refresh };
}
